import { useEffect, useState } from 'react';
import Button from '../../../components/button/button';
import SelectInput from "../../../components/forms/select";
import TextArea from "../../../components/forms/text-area";

import Message from "../../../components/toast-message";
import { useAppInfo } from '../../../helpers/hooks/common-hook';

import { getAgentCoreSettings, saveAgentCoreSettings, getAgentActions } from "../../../services/prompt-ai";

import { DEFAULT_LANGUAGE, LANGUAGES } from "./../../../consts/languages";
import PageContainer, { SettingsSection, SettingsActions } from "../components/settings-layout";

import TextBox from "../../../components/forms/text-box";
import SwitchComponent from "../../../components/switch";

const defaultAuthConfig = { "connector": null, "prompt": null };

function ActionSettings() {
  const appInfo = useAppInfo();
  const { appId } = appInfo;
  const [formState, setFormState] = useState({
    processing: false,
    success: false,
    failed: false,
    errorMessage: ''
  });

  const [settingsSaveState, setSettingsSaveState] = useState({
    processing: false,
    success: false,
    failed: false,
    errorMessage: ''
  });

  const [chatBotSetting, setChatBotSetting] = useState({
    agentName: '',
    agentDescription: '',
    defaultErrorMessage: '',
    outputFormat: '',
    kbTopResults: 20,
    botLanguage: DEFAULT_LANGUAGE,
    authConfig: defaultAuthConfig,
    vectorSearch: false,
    defaultWorkflow: {
      defaultSlots: {},
      triggerTexts: [],
      workflowId: ''
    }
  });

  const updateFormState = (processing, success, failed, errorMessage) => {
    setFormState(Object.assign({}, { processing, success, failed, errorMessage }))
  }

  const updateSettingsSaveState = (processing, success, failed, errorMessage) => {
    setSettingsSaveState(Object.assign({}, { processing, success, failed, errorMessage }))
  }

  async function fetchChatBotSettings() {
    updateFormState(true, false, false);
    const resp = await getAgentCoreSettings(appId);
    if (resp) {
      resp.authConfig = resp.authConfig || defaultAuthConfig;
      resp.defaultWorkflow = resp.defaultWorkflow || {
        defaultSlots: {},
        triggerTexts: [],
        workflowId: ''
      };
      setChatBotSetting(resp);
    }
    updateFormState(false, true, false);
  }

  useEffect(() => {
    fetchChatBotSettings();
  }, []);

  const onClickSaveConfig = async event => {
    event.preventDefault();
    if (!formState.processing && !settingsSaveState.processing) {
      updateSettingsSaveState(true, false, false);
      try {
        chatBotSetting.kbTopResults = parseInt(chatBotSetting.kbTopResults || 20);
        let { status, response } = await saveAgentCoreSettings(appId, chatBotSetting);
        if (status === 200) {
          updateSettingsSaveState(false, true, false);
        } else {
          updateSettingsSaveState(false, false, true, response);
        }

      } catch (e) {
        updateSettingsSaveState(false, false, true, 'Unable to save settings');
      }
    }
  }

  const handleChange = event => {
    const targetName = event.target.name;
    const targetValue = event.target.value;
    const newChatBotSetting = { ...chatBotSetting };
    newChatBotSetting[targetName] = targetValue
    setChatBotSetting(newChatBotSetting);
  };

  const handleVectorSearchChange = (value) => {
    setChatBotSetting({ ...chatBotSetting, vectorSearch: value });
  };

  const handleWorkflowChange = (event) => {
    const workflowId = event.target.value;
    setChatBotSetting({
      ...chatBotSetting,
      defaultWorkflow: {
        ...chatBotSetting.defaultWorkflow,
        workflowId
      }
    });
  };

  const handleTriggerTextsChange = (event) => {
    const triggerTextsString = event.target.value;
    const triggerTexts = triggerTextsString.split(',').map(text => text.trim()).filter(text => text);
    setChatBotSetting({
      ...chatBotSetting,
      defaultWorkflow: {
        ...chatBotSetting.defaultWorkflow,
        triggerTexts
      }
    });
  };

  const [workflows, setWorkflows] = useState([]);
  const [workflowsLoadingState, setWorkflowsLoadingState] = useState({
    processing: false,
    success: false,
    failed: false,
    message: '',
    errorMessage: ''
  });

  const updateWorkflowsLoading = ({ processing, success, failed, message, errorMessage }) => {
    setWorkflowsLoadingState({ processing, success, failed, message, errorMessage });
  }

  async function fetchWorkflows() {
    updateWorkflowsLoading({
      processing: true
    });
    const { data } = await getAgentActions(appId);
    if (data && Array.isArray(data)) {
      setWorkflows(data);
    }
    updateWorkflowsLoading({
      processing: false
    });
  }

  useEffect(() => {
    fetchWorkflows();
  }, []);

  const workflowOptions = workflows.map((workflow) => ({ value: workflow.id, label: workflow.name || workflow.id }));

  return (
    <PageContainer navigationPosition="below-header" title="Agent settings" subtitle="Shape your agent’s behavior, knowledge retrieval, and conversation defaults.">
      <form className="max-w-4xl space-y-6" onSubmit={onClickSaveConfig}>
        {settingsSaveState.success && <Message text="Your settings have been saved." type="success" />}
        {settingsSaveState.failed && <Message text="Unable to save settings. Please try again." type="error" />}
        <fieldset disabled={formState.processing || settingsSaveState.processing} className="min-w-0 space-y-6 disabled:opacity-60">
          <SettingsSection title="Agent behavior" description="Define instructions and the message to use when something goes wrong.">
                  {(appInfo && appInfo?.appMode !== "prompt_based") &&
                    <TextArea
                      type="text"
                      name="agentDescription"
                      id="agentDescription"
                      label="Instruction"
                      value={chatBotSetting.agentDescription}
                      placeholder="Add your custom instruction here"
                      onChange={handleChange}
                      rows={2}
                    />
                  }
                  
                  <TextArea
                      rootClass="pt-4"
                      type="text"
                      name="defaultErrorMessage"
                      id="defaultErrorMessage"
                      label="Default Error Message"
                      value={chatBotSetting.defaultErrorMessage}
                      placeholder="Add default error message"
                      onChange={handleChange}
                      rows={2}
                  />

                  </SettingsSection>
                  <SettingsSection title="Knowledge and language" description="Control how your agent retrieves information and responds.">
                  <TextBox
                      rootClass="w-full"
                      label="Knowledgebase Top Results"
                      type="number"
                      name="kbTopResults"
                      id="kbTopResults"
                      value={chatBotSetting.kbTopResults}
                      placeholder="Best matching top results count"
                      onChange={handleChange}
                  />

                  <div className="pt-6">
                    <SwitchComponent
                      title="Vector Search"
                      subTitle="Enable vector search for knowledge base queries"
                      enabled={chatBotSetting.vectorSearch}
                      onChange={handleVectorSearchChange}
                      loading={formState.processing}
                    />
                  </div>

                  <SelectInput
                    rootClass="w-full"
                    label="Language"
                    value={chatBotSetting.botLanguage}
                    name="botLanguage"
                    id="botLanguage"
                    onChange={handleChange}
                    optionsComp={<>
                      <option value="" disabled={true}>Select Language</option>
                      {LANGUAGES.map((language) =>
                        <option key={language} value={language}>{language}</option>
                      )}
                    </>}
                    required
                  />

                  </SettingsSection>
                  <SettingsSection title="Default workflow" description="Start a workflow when your agent receives one of the trigger phrases.">
                  <SelectInput
                    rootClass="w-full"
                    label="Workflow"
                    value={chatBotSetting.defaultWorkflow?.workflowId || ''}
                    name="workflowId"
                    id="workflowId"
                    onChange={handleWorkflowChange}
                    optionsComp={<>
                      <option value="">Select Workflow</option>
                      {workflowOptions.map((workflow) =>
                        <option key={workflow.value} value={workflow.value}>{workflow.label}</option>
                      )}
                    </>}
                  />

                  <TextBox
                    rootClass="w-full"
                    label="Trigger Texts"
                    type="text"
                    name="triggerTexts"
                    id="triggerTexts"
                    value={chatBotSetting.defaultWorkflow?.triggerTexts?.join(', ') || ''}
                    placeholder="Enter trigger texts separated by commas (e.g., hi, hello, hey)"
                    onChange={handleTriggerTextsChange}
                  />
          </SettingsSection>
          <SettingsActions>
            <Button type="submit" text="Save changes" loading={settingsSaveState.processing} disabled={formState.processing || settingsSaveState.processing || workflowsLoadingState.processing} />
          </SettingsActions>
        </fieldset>
      </form>
    </PageContainer>);
}

export default ActionSettings;
